import { useMemo, useState, useCallback } from 'react'

export interface SiteOption {
  id: number
  name: string
  assigned: boolean
}

export interface DepartmentOption {
  id: number
  name: string
  siteId: number | null
  siteName: string | null
  assigned: boolean
}

export interface UnitOption {
  id: number
  name: string
  departmentId: number | null
  siteId: number | null
  departmentName: string | null
  assigned: boolean
}

export type CheckState = true | false | 'indeterminate'

export interface TreeSite {
  id: number
  name: string
  departments: TreeDepartment[]
  unassignedUnits: UnitOption[]
}

export interface TreeDepartment {
  id: number
  name: string
  siteId: number | null
  siteName: string | null
  units: UnitOption[]
}

// Tree structure built from flat site/department/unit arrays.
export interface TreeData {
  sites: TreeSite[]
  unassignedDepartments: TreeDepartment[]
  unassignedUnits: UnitOption[]
}

export interface MaterializedIds {
  siteIds: number[]
  departmentIds: number[]
  unitIds: number[]
}

export function buildTree(
  sites: SiteOption[],
  departments: DepartmentOption[],
  units: UnitOption[],
): TreeData {
  const unitsByDepartment = new Map<number, UnitOption[]>()
  const unitsBySiteOnly: UnitOption[] = []
  const unassignedUnits: UnitOption[] = []

  for (const unit of units) {
    if (unit.departmentId != null) {
      const existing = unitsByDepartment.get(unit.departmentId) ?? []
      existing.push(unit)
      unitsByDepartment.set(unit.departmentId, existing)
    } else if (unit.siteId != null) {
      unitsBySiteOnly.push(unit)
    } else {
      unassignedUnits.push(unit)
    }
  }

  const departmentsBySite = new Map<number, TreeDepartment[]>()
  const unassignedDepartments: TreeDepartment[] = []

  for (const department of departments) {
    const treeDepartment: TreeDepartment = {
      id: department.id,
      name: department.name,
      siteId: department.siteId,
      siteName: department.siteName,
      units: unitsByDepartment.get(department.id) ?? [],
    }
    if (department.siteId != null) {
      const existing = departmentsBySite.get(department.siteId) ?? []
      existing.push(treeDepartment)
      departmentsBySite.set(department.siteId, existing)
    } else {
      unassignedDepartments.push(treeDepartment)
    }
  }

  const treeSites: TreeSite[] = sites.map((site) => ({
    id: site.id,
    name: site.name,
    departments: departmentsBySite.get(site.id) ?? [],
    unassignedUnits: unitsBySiteOnly.filter((unit) => unit.siteId === site.id),
  }))

  return { sites: treeSites, unassignedDepartments, unassignedUnits }
}

export function getAllUnitIdsForSite(site: TreeSite): number[] {
  const ids: number[] = []
  for (const department of site.departments) {
    for (const unit of department.units) {
      ids.push(unit.id)
    }
  }
  for (const unit of site.unassignedUnits) {
    ids.push(unit.id)
  }
  return ids
}

export function getAllUnitIdsForDepartment(department: TreeDepartment): number[] {
  return department.units.map((unit) => unit.id)
}

// A department or site is checked when it is assigned itself. Sites, departments and units are
// independent assignments (legacy keeps three separate lists), so a department can be assigned
// without all of its units and the tree must not infer one level from another (REBUILD-506).
export function computeDepartmentCheckState(
  department: TreeDepartment,
  selectedDepartmentIds: Set<number>,
  selectedUnitIds: Set<number>,
): CheckState {
  if (selectedDepartmentIds.has(department.id)) return true
  if (department.units.some((unit) => selectedUnitIds.has(unit.id))) return 'indeterminate'
  return false
}

export function computeSiteCheckState(
  site: TreeSite,
  selectedSiteIds: Set<number>,
  selectedDepartmentIds: Set<number>,
  selectedUnitIds: Set<number>,
): CheckState {
  if (selectedSiteIds.has(site.id)) return true
  const anyDepartment = site.departments.some((department) =>
    selectedDepartmentIds.has(department.id),
  )
  const anyUnit = getAllUnitIdsForSite(site).some((id) => selectedUnitIds.has(id))
  return anyDepartment || anyUnit ? 'indeterminate' : false
}

function toggleIds(previous: Set<number>, ids: number[], select: boolean): Set<number> {
  const next = new Set(previous)
  for (const id of ids) {
    if (select) {
      next.add(id)
    } else {
      next.delete(id)
    }
  }
  return next
}

function assignedIds(options: { id: number; assigned: boolean }[]): Set<number> {
  return new Set(options.filter((option) => option.assigned).map((option) => option.id))
}

export function useAccessTree(
  sites: SiteOption[],
  departments: DepartmentOption[],
  units: UnitOption[],
) {
  const tree = useMemo(() => buildTree(sites, departments, units), [sites, departments, units])

  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(() => assignedIds(sites))
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<Set<number>>(() =>
    assignedIds(departments),
  )
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<number>>(() => assignedIds(units))

  const toggleUnit = useCallback((unitId: number) => {
    setSelectedUnitIds((previous) => toggleIds(previous, [unitId], !previous.has(unitId)))
  }, [])

  const toggleDepartment = useCallback(
    (department: TreeDepartment) => {
      const select = !selectedDepartmentIds.has(department.id)
      setSelectedDepartmentIds((previous) => toggleIds(previous, [department.id], select))
      setSelectedUnitIds((previous) =>
        toggleIds(previous, getAllUnitIdsForDepartment(department), select),
      )
    },
    [selectedDepartmentIds],
  )

  const toggleSite = useCallback(
    (site: TreeSite) => {
      const select = !selectedSiteIds.has(site.id)
      setSelectedSiteIds((previous) => toggleIds(previous, [site.id], select))
      setSelectedDepartmentIds((previous) =>
        toggleIds(
          previous,
          site.departments.map((department) => department.id),
          select,
        ),
      )
      setSelectedUnitIds((previous) => toggleIds(previous, getAllUnitIdsForSite(site), select))
    },
    [selectedSiteIds],
  )

  const allSelected =
    selectedSiteIds.size === sites.length &&
    selectedDepartmentIds.size === departments.length &&
    selectedUnitIds.size === units.length

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedSiteIds(new Set())
      setSelectedDepartmentIds(new Set())
      setSelectedUnitIds(new Set())
    } else {
      setSelectedSiteIds(new Set(sites.map((site) => site.id)))
      setSelectedDepartmentIds(new Set(departments.map((department) => department.id)))
      setSelectedUnitIds(new Set(units.map((unit) => unit.id)))
    }
  }, [allSelected, sites, departments, units])

  const selectAllState = useMemo((): CheckState => {
    if (sites.length + departments.length + units.length === 0) return false
    if (allSelected) return true
    if (selectedSiteIds.size + selectedDepartmentIds.size + selectedUnitIds.size === 0) return false
    return 'indeterminate'
  }, [
    sites,
    departments,
    units,
    allSelected,
    selectedSiteIds,
    selectedDepartmentIds,
    selectedUnitIds,
  ])

  const materialized = useMemo(
    (): MaterializedIds => ({
      siteIds: [...selectedSiteIds].sort((a, b) => a - b),
      departmentIds: [...selectedDepartmentIds].sort((a, b) => a - b),
      unitIds: [...selectedUnitIds].sort((a, b) => a - b),
    }),
    [selectedSiteIds, selectedDepartmentIds, selectedUnitIds],
  )

  return {
    tree,
    selectedSiteIds,
    selectedDepartmentIds,
    selectedUnitIds,
    toggleUnit,
    toggleDepartment,
    toggleSite,
    toggleAll,
    selectAllState,
    materialized,
    computeDepartmentState: (department: TreeDepartment) =>
      computeDepartmentCheckState(department, selectedDepartmentIds, selectedUnitIds),
    computeSiteState: (site: TreeSite) =>
      computeSiteCheckState(site, selectedSiteIds, selectedDepartmentIds, selectedUnitIds),
  }
}
